import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import router from '@/router';

// Create axios instance with default config
// CRITICAL: This instance is used by auth store for authentication endpoints
// Final URL construction: baseURL + path
// Dev: https://api.banjinos.dev/api + /v1/admin/auth/login
// Prod: https://api.banjinos.com/api + /v1/admin/auth/login
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || (window.location.hostname.includes('.com') ? 'https://api.banjinos.com/api' : 'https://api.banjinos.dev/api'),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    
    // Add auth token if available
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`;
    }
    
    // Add client type header if needed
    if (authStore.clientType) {
      config.headers['X-Client-Type'] = authStore.clientType;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const authStore = useAuthStore();
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      if (authStore.refreshToken) {
        try {
          const newToken = await authStore.refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          authStore.clearAuth();
          router.push('/login');
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        authStore.clearAuth();
        router.push('/login');
      }
    }
    
    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      // Could redirect to an error page or show a notification
    }
    
    // Handle 429 Too Many Requests
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      console.error(`Rate limited. Retry after ${retryAfter} seconds`);
      // Could implement automatic retry logic here
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      // Could show offline notification
    }
    
    return Promise.reject(error);
  }
);

// Helper methods
export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

export const setClientType = (clientType) => {
  if (clientType) {
    axiosInstance.defaults.headers.common['X-Client-Type'] = clientType;
  } else {
    delete axiosInstance.defaults.headers.common['X-Client-Type'];
  }
};

// Export configured instance as default
export default axiosInstance;